/**
 * Aprimo video playback (MP4 / HLS).
 *
 * Additive helper: the players check `isAprimo( url )` first and hand Aprimo links
 * to `attachAprimoVideo()`. Everything else (Cloudflare Stream) falls through to the
 * existing hls.js code untouched.
 *
 * Mirror of Matat_Pandora_Aprimo_Video (PHP).
 */

const APRIMO_PREFIX = 'https://p3.aprimocdn.net/pandora/';
const COMMA_TOKEN = '~c~';

/**
 * Restore commas from the storage token.
 *
 * @param {string} url Stored value.
 * @return {string} URL.
 */
export function decodeAprimoUrl(url) {
	return String(url).split(COMMA_TOKEN).join(',');
}

/**
 * Replace commas with the storage token.
 *
 * @param {string} url URL.
 * @return {string} Stored value.
 */
export function encodeAprimoUrl(url) {
	return String(url).split(',').join(COMMA_TOKEN);
}

/**
 * Playback type of an Aprimo link.
 *
 * @param {string} url Aprimo URL (encoded or not).
 * @return {string} 'mp4', 'hls' or ''.
 */
export function aprimoType(url) {
	if (typeof url !== 'string') return '';

	const path = decodeAprimoUrl(url).split(/[?#]/)[0];

	if (/\.m3u8$/i.test(path)) return 'hls';
	if (/\.mp4$/i.test(path)) return 'mp4';

	return '';
}

/**
 * Whether a URL is an Aprimo MP4 / HLS link.
 *
 * @param {string} url URL.
 * @return {boolean} True for Aprimo.
 */
export function isAprimo(url) {
	return typeof url === 'string' && url.indexOf(APRIMO_PREFIX) === 0 && aprimoType(url) !== '';
}

/**
 * Poster image for an Aprimo link (mirror of Matat_Pandora_Aprimo_Video::poster()).
 *
 * MP4: "..._mp4_720p.mp4" -> "..._mp4_720p/poster.jpg".
 * HLS: the .m3u8 file name is replaced with poster.jpg.
 *
 * @param {string} url Aprimo URL (encoded or not).
 * @return {string} Poster URL or ''.
 */
export function aprimoPoster(url) {
	if (!isAprimo(url)) return '';

	const src = decodeAprimoUrl(url);
	const poster = aprimoType(src) === 'hls'
		? src.replace(/\/[^/?#]+\.m3u8([?#].*)?$/i, '/poster.jpg')
		: src.replace(/_mp4_(720p|480p)\.mp4$/i, '_mp4_$1/poster.jpg');

	return poster !== src ? poster : '';
}

/**
 * Attach an Aprimo video to a <video> element.
 *
 * MP4: native `video.src`. HLS: a new hls.js instance where supported, otherwise native `video.src`.
 * Videos are set muted / loop / playsInline like the existing Cloudflare players.
 *
 * @param {HTMLVideoElement} video           Video element.
 * @param {string}           url             Aprimo URL.
 * @param {Object}           [options]
 * @param {Function}         [options.onReady] Called once the media can start (MANIFEST_PARSED / loadedmetadata).
 * @return {Object|null} The hls.js instance, or null when native playback is used.
 */
export function attachAprimoVideo(video, url, options = {}) {
	if (!video || !isAprimo(url)) return null;

	const src = decodeAprimoUrl(url);
	const onReady = typeof options.onReady === 'function' ? options.onReady : null;

	// Already attached to this URL (players can be re-initialised on AJAX refresh).
	if (video.dataset.aprimoSrc === src) return video.aprimoHls || null;

	if (video.aprimoHls) {
		video.aprimoHls.destroy();
		video.aprimoHls = null;
	}
	video.dataset.aprimoSrc = src;

	video.muted = true;
	video.loop = true;
	video.playsInline = true;

	applyAprimoRatio(video);

	// Poster for videos rendered without one (JS-built markup, blocks without a poster image).
	if (!video.getAttribute('poster')) {
		const poster = aprimoPoster(src);
		if (poster) {
			video.setAttribute('poster', poster);
		}
	}

	const Hls = window.Hls;

	if (aprimoType(src) === 'hls' && Hls && Hls.isSupported()) {
		const hls = new Hls({
			enableWorker: true,
			backBufferLength: 90,
		});

		hls.loadSource(src);
		hls.attachMedia(video);

		hls.on(Hls.Events.MANIFEST_PARSED, function () {
			if (onReady) onReady(hls);
		});

		hls.on(Hls.Events.ERROR, function (event, data) {
			if (!data.fatal) return;

			switch (data.type) {
				case Hls.ErrorTypes.NETWORK_ERROR:
					console.error('Aprimo video network error:', data);
					hls.startLoad();
					break;
				case Hls.ErrorTypes.MEDIA_ERROR:
					console.error('Aprimo video media error:', data);
					hls.recoverMediaError();
					break;
				default:
					console.error('Aprimo video fatal error:', data);
					hls.destroy();
					break;
			}
		});

		video.aprimoHls = hls;

		return hls;
	}

	// MP4 everywhere, and HLS on browsers with native support (Safari / iOS).
	// An MP4 rendered with its `src` already in the markup (gallery) is left alone:
	// re-assigning the same src would restart the download.
	const alreadyLoading = video.getAttribute('src') === src;

	if (onReady) {
		if (alreadyLoading && video.readyState >= 1) {
			onReady(null);
		} else {
			video.addEventListener('loadedmetadata', function () {
				onReady(null);
			}, { once: true });
		}
	}

	if (!alreadyLoading) {
		video.src = src;
	}

	return null;
}

/**
 * Expose the video's real aspect ratio as `--aprimo-ratio` (the CSS falls back to the
 * file-name suffix rendered by PHP, e.g. _3x4, until the metadata is known).
 *
 * @param {HTMLVideoElement} video Video element.
 */
export function applyAprimoRatio(video) {
	const set = () => {
		if (video.videoWidth && video.videoHeight) {
			video.style.setProperty('--aprimo-ratio', video.videoWidth + ' / ' + video.videoHeight);
		}
	};

	if (video.readyState >= 1) {
		set();
	}
	video.addEventListener('loadedmetadata', set);
}

/**
 * Generic HLS attach, used only for lazily-started gallery copies (lightbox / thumbnails)
 * of non-Aprimo (Cloudflare) videos. Same hls.js settings as the existing Cloudflare players;
 * the eagerly-loaded Cloudflare videos keep using their original code.
 *
 * @param {HTMLVideoElement} video           Video element.
 * @param {string}           url             HLS manifest URL.
 * @param {Object}           [options]
 * @param {Function}         [options.onReady] Called once the media can start.
 * @return {Object|null} The hls.js instance, or null when native playback is used.
 */
export function attachHlsVideo(video, url, options = {}) {
	const onReady = typeof options.onReady === 'function' ? options.onReady : null;
	const Hls = window.Hls;

	if (Hls && Hls.isSupported()) {
		const hls = new Hls({
			enableWorker: true,
			lowLatencyMode: true,
			backBufferLength: 90
		});

		hls.loadSource(url);
		hls.attachMedia(video);

		hls.on(Hls.Events.MANIFEST_PARSED, function () {
			video.muted = true;
			video.loop = true;
			video.playsInline = true;
			if (onReady) onReady(hls);
		});

		hls.on(Hls.Events.ERROR, function (event, data) {
			if (!data.fatal) return;

			switch (data.type) {
				case Hls.ErrorTypes.NETWORK_ERROR:
					hls.startLoad();
					break;
				case Hls.ErrorTypes.MEDIA_ERROR:
					hls.recoverMediaError();
					break;
				default:
					hls.destroy();
					break;
			}
		});

		return hls;
	}

	if (video.canPlayType('application/vnd.apple.mpegurl')) {
		video.addEventListener('loadedmetadata', function () {
			video.muted = true;
			video.loop = true;
			video.playsInline = true;
			if (onReady) onReady(null);
		}, { once: true });
		video.src = url;
	}

	return null;
}

/**
 * Undo attachAprimoVideo() (editor previews re-attach when the URL changes).
 *
 * @param {HTMLVideoElement} video Video element.
 */
export function detachAprimoVideo(video) {
	if (!video) return;

	if (video.aprimoHls) {
		video.aprimoHls.destroy();
		video.aprimoHls = null;
	} else if (video.dataset.aprimoSrc) {
		video.removeAttribute('src');
		video.load();
	}

	delete video.dataset.aprimoSrc;
}

// Global for scripts that are not bundled (e.g. src/js/product-loop-banner/frontend.js).
if (typeof window !== 'undefined') {
	window.MatatPandoraAprimo = window.MatatPandoraAprimo || {
		isAprimo,
		aprimoType,
		aprimoPoster,
		encodeAprimoUrl,
		decodeAprimoUrl,
		attachAprimoVideo,
		detachAprimoVideo,
	};
}
